import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, Card, Grid, Text } from '@aws-amplify/ui-react';
import PageBaseNoSignIn from '@components/pages/PageBaseNoSignIn';
import { uploadData } from 'aws-amplify/storage';
import { useSetRecoilState } from 'recoil';

import { registGroupApi } from '@apis/index';
import { MessageDialogState } from '@atom/DialogState';
import { LoadingState } from '@atom/LoadingState';

import { RegistGroupMutationVariables } from '../../../API';

const GroupConfirmPage = () => {
  /* 共通State */
  const setMessageDialogState = useSetRecoilState(MessageDialogState);
  const setLoading = useSetRecoilState(LoadingState);

  const location = useLocation();
  const { confirmParam } = location.state;
  const navigate = useNavigate();

  // ファイルアップロード処理
  const uploadFile = async (nameListFile: File, path: string) => {
    setLoading(true);
    try {
      await uploadData({
        key: path,
        data: nameListFile,
        options: {
          accessLevel: 'guest' // defaults to `guest` but can be 'private' | 'protected' | 'guest'
          // onprogress // Optional progress callback.
        }
      }).result;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw new Error('ファイルアップロードエラー');
    } finally {
      setLoading(false);
    }
  };

  // 修正するボタン押下時の処理
  const fix = () => {
    const fixParam = confirmParam;
    navigate('/c002/register', { state: { fixParam } });
  };

  // ファイルダウンロード処理
  const fileListArray: File[] = Array.from(confirmParam.nameListFiles ?? []);
  const downloadFile = (file_index: number) => {
    const file = confirmParam.nameListFiles[file_index];
    if (file) {
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
    }
  };

  // ファイルダウンロードボタン表示処理
  const displayFileNames = fileListArray.map((file: File, index: number) => (
    <Button
      variation="link"
      color="black"
      style={{ display: 'block', marginBottom: '8px' }}
      onClick={() => downloadFile(index)}
    >
      {file.name}
    </Button>
  ));

  // 登録ボタン押下時の処理
  const register = async () => {
    setLoading(true);
    try {
      // 現在日時取得
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const nowStr = `${year}${month}${day}${hours}${minutes}${seconds}`;

      // ファイルアップロード
      const uploadFiles = [];
      const pathList: string[] = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const file of fileListArray) {
        const path = `${confirmParam.groupName}/${nowStr}/${file.name}`;
        pathList.push(path);
        uploadFiles.push(uploadFile(file, path));
      }
      // 並列に処理
      await Promise.all(uploadFiles);

      // API実行
      // API実行用パラメータ
      const input: RegistGroupMutationVariables = {
        input: {
          // Todo 施設によってfacility_id変える
          facilityId: confirmParam.facilityId,
          groupName: confirmParam.groupName,
          representative: confirmParam.representativeName,
          zipCode: confirmParam.zipCode,
          adress: confirmParam.adress,
          tel: confirmParam.phoneNumber,
          email: confirmParam.email,
          nameList01: pathList[0],
          nameList02: pathList[1],
          nameList03: pathList[2]
        }
      };
      await registGroupApi(input);

      // 登録完了後、遷移
      navigate('/c002/complete');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error(error);
      let message = '時間をおいてから再度お試しください。解決できない場合は管理者へお問い合わせください。';
      if ('errors' in error && error.errors.length !== 0) {
        message = error.errors[0].message;
      }
      setMessageDialogState({
        open: true,
        messageData: {
          title: 'エラーが発生しました。',
          message
        }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageBaseNoSignIn title="申請内容の確認">
      <Grid templateColumns={{ base: '1fr', large: '1fr' }}>
        <Card>
          <Text as="p" fontWeight={400} color="secondary" fontSize="1em" width="100%">
            団体名
          </Text>
          <Text as="p" fontWeight={400} fontSize="1.3em" width="100%">
            {confirmParam.groupName}
          </Text>
        </Card>
        <Card>
          <Text as="p" fontWeight={400} color="secondary" fontSize="1em" width="100%">
            代表者氏名
          </Text>
          <Text as="p" fontWeight={400} fontSize="1.3em" width="100%">
            {confirmParam.representativeName}
          </Text>
        </Card>
        <Card>
          <Text as="p" fontWeight={400} color="secondary" fontSize="1em" width="100%">
            電話番号
          </Text>
          <Text as="p" fontWeight={400} fontSize="1.3em" width="100%">
            {confirmParam.phoneNumber}
          </Text>
        </Card>
        <Card>
          <Text as="p" fontWeight={400} color="secondary" fontSize="1em" width="100%">
            郵便番号
          </Text>
          <Text as="p" fontWeight={400} fontSize="1.3em" width="100%">
            {confirmParam.zipCode}
          </Text>
        </Card>
        <Card>
          <Text as="p" fontWeight={400} color="secondary" fontSize="1em" width="100%">
            住所
          </Text>
          <Text as="p" fontWeight={400} fontSize="1.3em" width="100%">
            {confirmParam.adress}
          </Text>
        </Card>
        <Card>
          <Text as="p" fontWeight={400} color="secondary" fontSize="1em" width="100%">
            団体名簿
          </Text>
          {displayFileNames}
        </Card>
        <Card>
          <Button variation="primary" isFullWidth onClick={register}>
            この内容で団体登録申請する
          </Button>
        </Card>
        <Card>
          <Button variation="link" borderColor="var(--amplify-colors-teal-80)" isFullWidth onClick={fix}>
            修正する
          </Button>
        </Card>
      </Grid>
    </PageBaseNoSignIn>
  );
};

export default GroupConfirmPage;
